import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Power,
  Visibility,
  Settings,
  AdminPanelSettings,
  People,
  Backup
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import Cookies from 'js-cookie';
import EnvironmentPermissionsDialog from '../components/Environment/EnvironmentPermissionsDialog';
import BackupDialog from '../components/Environment/BackupDialog';
import { useHealth } from '../contexts/HealthContext';
import HealthStatusChip from '../components/HealthStatusChip';

const EnvironmentManagementPage = () => {
  const { user, environments, loadEnvironments } = useAuth();
  const { 
    allEnvironmentsHealth, 
    isChecking, 
    getHealthDisplay, 
    refreshAllEnvironments, 
    setEnvironments,
    startAllEnvironmentsMonitoring,
    stopAllEnvironmentsMonitoring
  } = useHealth();
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEnvironment, setEditingEnvironment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    api_url: '',
    api_key: ''
  });
  
  // Permissions dialog state
  const [permissionsDialog, setPermissionsDialog] = useState({
    open: false,
    environment: null
  });

  // Backup dialog state
  const [backupDialog, setBackupDialog] = useState({
    open: false,
    environment: null
  });

  useEffect(() => {
    refreshEnvironments();
  }, []);

  // Sync environments met HealthContext
  useEffect(() => {
    if (environments.length > 0) {
      setEnvironments(environments);
    }
  }, [environments, setEnvironments]);

  // Start all environments monitoring wanneer component mount, stop bij unmount
  useEffect(() => {
    startAllEnvironmentsMonitoring();
    return () => {
      stopAllEnvironmentsMonitoring();
    };
  }, [startAllEnvironmentsMonitoring, stopAllEnvironmentsMonitoring]);

  const refreshEnvironments = async () => {
    setLoading(true);
    try {
      await loadEnvironments();
    } catch (error) {
      showSnackbar('Fout bij laden van environments', 'error');
    }
    setLoading(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = (environment = null) => {
    setEditingEnvironment(environment);
    setFormData(environment ? {
      name: environment.name,
      description: environment.description,
      api_url: environment.api_url,
      api_key: '' // Veiligheid: API key niet pre-vullen
    } : {
      name: '',
      description: '',
      api_url: '',
      api_key: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEnvironment(null);
    setFormData({ name: '', description: '', api_url: '', api_key: '' });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      const token = Cookies.get('auth_token');
              const url = editingEnvironment
          ? `${API_CONFIG.centralBackendURL}/api/v1/environments/${editingEnvironment.id}`
          : `${API_CONFIG.centralBackendURL}/api/v1/environments`;
      
      const method = editingEnvironment ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      await refreshEnvironments();
      handleCloseDialog();
      showSnackbar(
        editingEnvironment ? 'Environment bijgewerkt!' : 'Environment toegevoegd!',
        'success'
      );
    } catch (error) {
      console.error('Error saving environment:', error);
      showSnackbar('Fout bij opslaan environment', 'error');
    }
    setLoading(false);
  };

  const handleDelete = async (environmentId, environmentName) => {
    if (!window.confirm(`Weet je zeker dat je "${environmentName}" wilt verwijderen?`)) {
      return;
    }

    try {
      setLoading(true);
      const token = Cookies.get('auth_token');
      
              const response = await fetch(
          `${API_CONFIG.centralBackendURL}/api/v1/environments/${environmentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      await refreshEnvironments();
      showSnackbar('Environment verwijderd!', 'success');
    } catch (error) {
      console.error('Error deleting environment:', error);
      showSnackbar('Fout bij verwijderen environment', 'error');
    }
    setLoading(false);
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'readonly': return <Visibility fontSize="small" />;
      case 'operator': return <Settings fontSize="small" />;
      case 'admin': return <Edit fontSize="small" />;
      case 'owner': return <AdminPanelSettings fontSize="small" />;
      default: return <Visibility fontSize="small" />;
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'readonly': return 'default';
      case 'operator': return 'primary';
      case 'admin': return 'secondary';
      case 'owner': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'maintenance': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Actief';
      case 'inactive': return 'Inactief';
      case 'maintenance': return 'Onderhoud';
      case 'error': return 'Fout';
      default: return status;
    }
  };

  const canEdit = (environment) => {
    return ['owner', 'admin'].includes(environment.user_permission);
  };

  const canDelete = (environment) => {
    return environment.user_permission === 'owner';
  };

  const handleOpenPermissions = (environment) => {
    setPermissionsDialog({
      open: true,
      environment: environment
    });
  };

  const handleClosePermissions = () => {
    setPermissionsDialog({
      open: false,
      environment: null
    });
  };

  const canManagePermissions = (environment) => {
    return ['owner', 'admin'].includes(environment.user_permission);
  };

  const canManageBackups = (environment) => {
    // Alleen super admins kunnen backups beheren
    return user?.system_role === 'super_admin';
  };

  const handleOpenBackup = (environment) => {
    setBackupDialog({
      open: true,
      environment: environment
    });
  };

  const handleCloseBackup = () => {
    setBackupDialog({
      open: false,
      environment: null
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Environment Beheer
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Beheer uw electrolyzer omgevingen
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={refreshEnvironments}
              disabled={loading}
            >
              Vernieuwen
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={refreshAllEnvironments}
              disabled={isChecking}
              color="secondary"
            >
              {isChecking ? 'Controleren...' : 'Health Check'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Environment Toevoegen
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {environments.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Totaal Environments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {environments.filter(env => {
                  const health = getHealthDisplay(env.id);
                  return health.status === 'healthy';
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Online Environments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="secondary.main">
                {environments.filter(env => env.user_permission === 'owner').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Eigenaar van
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {environments.filter(env => {
                  const health = getHealthDisplay(env.id);
                  return ['unhealthy', 'unreachable', 'error'].includes(health.status);
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Met Problemen
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Environments Table */}
      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Naam</TableCell>
                  <TableCell>Beschrijving</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Health Status</TableCell>
                  <TableCell>Uw Rechten</TableCell>
                  <TableCell>Eigenaar</TableCell>
                  <TableCell>Laatst Gecontroleerd</TableCell>
                  <TableCell align="right">Acties</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {environments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Geen environments gevonden
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  environments.map((environment) => {
                    const healthDisplay = getHealthDisplay(environment.id);
                    const healthData = allEnvironmentsHealth[environment.id];
                    
                    return (
                      <TableRow key={environment.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Power color="primary" fontSize="small" />
                            <Typography variant="subtitle2">
                              {environment.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {environment.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <HealthStatusChip 
                            environmentId={environment.id}
                            showDetails={true}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getPermissionIcon(environment.user_permission)}
                            label={environment.user_permission}
                            color={getPermissionColor(environment.user_permission)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {environment.owner?.name || environment.owner?.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {healthData?.lastCheck 
                              ? healthData.lastCheck.toLocaleString('nl-NL')
                              : (environment.last_health_check 
                                ? new Date(environment.last_health_check).toLocaleString('nl-NL')
                                : 'Nooit')
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {canManageBackups(environment) && (
                            <IconButton
                              size="small"
                              onClick={() => handleOpenBackup(environment)}
                              color="info"
                              title="Backup beheer"
                            >
                              <Backup fontSize="small" />
                            </IconButton>
                          )}
                          {canManagePermissions(environment) && (
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPermissions(environment)}
                              color="secondary"
                              title="Beheer toegangsrechten"
                            >
                              <People fontSize="small" />
                            </IconButton>
                          )}
                          {canEdit(environment) && (
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(environment)}
                              color="primary"
                              title="Bewerken"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                          {canDelete(environment) && (
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(environment.id, environment.name)}
                              color="error"
                              title="Verwijderen"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEnvironment ? 'Environment Bewerken' : 'Nieuw Environment Toevoegen'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Naam *"
              fullWidth
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="bijv. Electrolyzer Hoofdkantoor"
            />
            <TextField
              label="Beschrijving"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Beschrijf deze electrolyzer omgeving..."
            />
            <TextField
              label="API URL *"
              fullWidth
              value={formData.api_url}
              onChange={(e) => handleFormChange('api_url', e.target.value)}
              placeholder="http://192.168.1.100:8000"
              helperText="De URL van de electrolyzer API"
            />
            <TextField
              label={editingEnvironment ? "Nieuwe API Key (laat leeg om ongewijzigd te laten)" : "API Key *"}
              fullWidth
              type="password"
              value={formData.api_key}
              onChange={(e) => handleFormChange('api_key', e.target.value)}
              placeholder="Uw API sleutel"
              helperText="De API sleutel voor authenticatie"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Annuleren
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name || !formData.api_url || (!editingEnvironment && !formData.api_key)}
          >
            {loading ? <CircularProgress size={20} /> : (editingEnvironment ? 'Bijwerken' : 'Toevoegen')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Environment Permissions Dialog */}
      <EnvironmentPermissionsDialog
        open={permissionsDialog.open}
        onClose={handleClosePermissions}
        environment={permissionsDialog.environment}
        onUpdate={refreshEnvironments}
      />

      {/* Backup Dialog */}
      <BackupDialog
        open={backupDialog.open}
        onClose={handleCloseBackup}
        environment={backupDialog.environment}
      />
    </Box>
  );
};

export default EnvironmentManagementPage; 